import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  UserCheck, UserX, Search, Loader2, ArrowLeft, ArrowRight,
  CalendarDays, Clock, MapPin, Users,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}

export default function EventAttendanceModal({ open, onClose, event, cohortId }) {
  const [roster, setRoster] = useState([]);         // all learners in cohort
  const [checkins, setCheckins] = useState([]);     // rows from /events/{id}/checkins
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async (opts = {}) => {
    if (!event || !cohortId) return;
    // `silent` keeps the list visible during polling refreshes so the UI
    // doesn't flicker every 4 seconds.
    if (!opts.silent) setLoading(true);
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API}/cohorts/${cohortId}/learners`, { withCredentials: true }),
        axios.get(`${API}/events/${event.id}/checkins`),
      ]);
      setRoster(r.data);
      setCheckins(c.data);
    } catch {
      if (!opts.silent) toast.error("Failed to load attendance");
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, [event, cohortId]);

  // Initial load whenever the modal opens.
  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  // Live polling: while the modal is open, silently re-fetch the check-in
  // list every 4 seconds so new kiosk check-ins appear in real time
  // without the admin having to click refresh. Stops the moment the modal
  // closes. (Lightweight alternative to a WebSocket — no backend plumbing
  // required; same-origin JSON request is cheap.)
  useEffect(() => {
    if (!open) return undefined;
    const handle = setInterval(() => {
      refresh({ silent: true });
    }, 4000);
    return () => clearInterval(handle);
  }, [open, refresh]);

  const checkedInIds = new Set(checkins.map((c) => c.learner_id));
  const q = search.trim().toLowerCase();
  const matchQ = (c) =>
    !q || `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q);

  const checkedInList = checkins
    .map((ci) => ({
      id: ci.learner_id,
      first_name: ci.first_name,
      last_name: ci.last_name,
      timestamp: ci.timestamp,
      walkin: ci.walkin,
    }))
    .filter(matchQ);

  const notCheckedIn = roster.filter((c) => !checkedInIds.has(c.id)).filter(matchQ);

  const checkIn = async (learnerId) => {
    setBusyId(learnerId);
    try {
      await axios.post(`${API}/events/${event.id}/checkin`, { learner_id: learnerId });
      toast.success("Marked as checked in");
      await refresh();
    } catch {
      toast.error("Failed to check in");
    } finally {
      setBusyId(null);
    }
  };

  const undoCheckIn = async (learnerId) => {
    setBusyId(learnerId);
    try {
      await axios.delete(`${API}/events/${event.id}/checkin/${learnerId}`, { withCredentials: true });
      toast.success("Removed from checked-in list");
      await refresh();
    } catch {
      toast.error("Failed to undo");
    } finally {
      setBusyId(null);
    }
  };

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] p-0 flex flex-col overflow-hidden bg-white border-gray-200"
        data-testid="event-attendance-modal"
      >
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0 border-b border-gray-200">
          <DialogTitle className="text-gray-900 font-heading text-xl font-bold">
            Attendance — {event.name}
          </DialogTitle>
          <DialogDescription className="text-gray-600 font-body text-xs flex flex-wrap gap-x-4 gap-y-1 mt-1">
            <span className="flex items-center gap-1.5"><CalendarDays className="h-3 w-3" />{event.date}</span>
            {event.start_time && <span className="flex items-center gap-1.5"><Clock className="h-3 w-3" />{event.start_time}{event.end_time ? ` – ${event.end_time}` : ""}</span>}
            {event.location && <span className="flex items-center gap-1.5"><MapPin className="h-3 w-3" />{event.location}</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-3 shrink-0 border-b border-gray-200 bg-gray-50/50">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by name or email…"
              data-testid="attendance-search"
              className="pl-10 h-9 bg-white border-gray-300 text-gray-900 text-sm placeholder:text-gray-400"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50/30">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {/* Checked in */}
              <Column
                title="Checked in"
                icon={UserCheck}
                iconColor="#10b981"
                count={checkedInList.length}
                total={checkins.length}
                testid="attendance-checked-in"
              >
                {checkedInList.length === 0 ? (
                  <EmptyHint text={search ? "No matches in this list." : "No one has checked in yet."} />
                ) : (
                  checkedInList.map((c) => (
                    <Row
                      key={`in-${c.id}`}
                      testid={`attendance-in-${c.id}`}
                      first={c.first_name}
                      last={c.last_name}
                      meta={<><Clock className="h-3 w-3" /> {fmtTime(c.timestamp)}</>}
                      walkin={c.walkin}
                      actionIcon={ArrowRight}
                      actionLabel="Move to not checked in"
                      actionColor="#ef4444"
                      onAction={() => undoCheckIn(c.id)}
                      busy={busyId === c.id}
                    />
                  ))
                )}
              </Column>

              {/* Not checked in */}
              <Column
                title="Not checked in"
                icon={UserX}
                iconColor="#6b7280"
                count={notCheckedIn.length}
                total={roster.length - checkins.length}
                testid="attendance-not-checked-in"
              >
                {notCheckedIn.length === 0 ? (
                  <EmptyHint text={search ? "No matches in this list." : "Everyone in the roster has checked in."} />
                ) : (
                  notCheckedIn.map((c) => (
                    <Row
                      key={`out-${c.id}`}
                      testid={`attendance-out-${c.id}`}
                      first={c.first_name}
                      last={c.last_name}
                      meta={c.email ? <>{c.email}</> : null}
                      walkin={c.is_walkin_pending}
                      actionIcon={ArrowLeft}
                      actionLabel="Move to checked in"
                      actionColor="#10b981"
                      onAction={() => checkIn(c.id)}
                      busy={busyId === c.id}
                      reverse
                    />
                  ))
                )}
              </Column>
            </div>
          )}
        </div>

        <div className="px-6 py-3 shrink-0 border-t border-gray-200 bg-white flex justify-between items-center">
          <span className="text-xs font-body text-gray-600 flex items-center gap-1.5">
            <Users className="h-3 w-3" />
            {checkins.length} / {roster.length} checked in
          </span>
          <Button variant="ghost" onClick={onClose} className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 text-sm" data-testid="attendance-close">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Column({ title, icon: Icon, iconColor, count, total, testid, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/70">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4" style={{ color: iconColor }} />
          <h4 className="font-heading text-sm font-bold text-gray-900">{title}</h4>
        </div>
        <Badge className="bg-gray-100 text-gray-700 border-gray-200 text-xs hover:bg-gray-100">
          {count}{typeof total === "number" && total !== count ? ` / ${total}` : ""}
        </Badge>
      </div>
      <div className="divide-y divide-gray-100 max-h-[55vh] overflow-y-auto">{children}</div>
    </div>
  );
}

function Row({ testid, first, last, meta, walkin, actionIcon: Icon, actionLabel, actionColor, onAction, busy, reverse }) {
  return (
    <div className={`flex items-center gap-2 px-4 py-2.5 hover:bg-gray-50 ${reverse ? "flex-row" : "flex-row"}`} data-testid={testid}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-body text-gray-900 truncate">{first} {last}</p>
          {walkin && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[9px] uppercase tracking-wider hover:bg-amber-100">walk-in</Badge>
          )}
        </div>
        {meta && (
          <p className="text-[11px] font-body text-gray-500 flex items-center gap-1 mt-0.5 truncate">
            {meta}
          </p>
        )}
      </div>
      <button
        onClick={onAction}
        disabled={busy}
        title={actionLabel}
        className="shrink-0 p-2 rounded hover:bg-gray-100 disabled:opacity-50 transition-colors"
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
        ) : (
          <Icon className="h-4 w-4" style={{ color: actionColor }} />
        )}
      </button>
    </div>
  );
}

function EmptyHint({ text }) {
  return <p className="px-4 py-6 text-xs font-body text-gray-500 text-center">{text}</p>;
}
